import { useEffect, useRef } from "react";
import { BODY_FONTS, DISPLAY_FONTS, PREVIEW_DIMENSIONS } from "../constants";
import { renderStudioFrame } from "../lib/renderer";
import type { RenderResult, StudioState } from "../types";

interface StudioCanvasProps {
  state: StudioState;
  paused: boolean;
  restartKey: number;
  onRenderInfo: (result: RenderResult) => void;
}

export function StudioCanvas({
  state,
  paused,
  restartKey,
  onRenderInfo
}: StudioCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const startedAt = useRef(0);
  const pausedProgress = useRef(0);
  const lastResult = useRef("");

  useEffect(() => {
    startedAt.current = performance.now();
    pausedProgress.current = 0;
  }, [restartKey]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dimensions = PREVIEW_DIMENSIONS[state.output];
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let animationFrame = 0;
    let cancelled = false;

    const draw = (now: number) => {
      const durationMs = state.animation.duration * 1000;
      const liveProgress = ((now - startedAt.current) % durationMs) / durationMs;
      if (!paused) pausedProgress.current = liveProgress;
      const progress = reducedMotion ? 0 : paused ? pausedProgress.current : liveProgress;
      const result = renderStudioFrame(context, state, { progress });
      const signature = `${result.overflow}:${result.headlineLines.join("|")}:${Math.round(result.headlineSize)}`;
      if (signature !== lastResult.current) {
        lastResult.current = signature;
        onRenderInfo(result);
      }
      animationFrame = requestAnimationFrame(draw);
    };

    const start = async () => {
      const displayFont = DISPLAY_FONTS[state.typography.displayFont].family;
      const bodyFont = BODY_FONTS[state.typography.bodyFont].family;
      try {
        await Promise.all([
          document.fonts.load(`400 100px ${displayFont}`),
          document.fonts.load(`400 24px ${bodyFont}`),
          document.fonts.load(`700 24px ${bodyFont}`)
        ]);
      } catch {
        // The renderer retains a generic fallback if a local font cannot load.
      }
      if (cancelled) return;
      startedAt.current = performance.now();
      animationFrame = requestAnimationFrame(draw);
    };

    void start();
    return () => {
      cancelled = true;
      cancelAnimationFrame(animationFrame);
    };
  }, [onRenderInfo, paused, restartKey, state]);

  return (
    <div className="preview-frame" data-testid="preview-frame">
      <canvas
        ref={canvasRef}
        className="block h-auto max-h-[calc(100vh-190px)] max-w-full rounded-[10px]"
        aria-label="Live Wavelet Audio marketing asset preview"
        role="img"
        data-testid="studio-canvas"
      />
    </div>
  );
}
